/**
 * External / satellite systems glossary — populated by the Analysis Agent (LLM),
 * not a fixed product catalog. Each analysis run identifies whatever systems
 * appear in that customer's readiness package and describes them in plain English.
 */

function slugId(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 48);
}

function normalizeAgentSystem(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const name = String(raw.name || '').trim();
  if (!name) return null;
  const id = String(raw.id || slugId(name)).trim() || slugId(name);
  const description = String(raw.description || '').trim();
  const identificationSource = String(
    raw.identification_source || raw.source || '',
  ).trim();
  return {
    id,
    name,
    description: description || identificationSource || name,
    category: String(raw.category || 'Connected system').trim(),
    role: String(raw.role || 'other').trim(),
    identificationSource,
    nodeIds: Array.isArray(raw.node_ids)
      ? raw.node_ids.map((x) => String(x).trim()).filter(Boolean)
      : [],
    fromAgent: true,
  };
}

/** Glossary rows authored by the Analysis Agent during offline analysis. */
export function getExternalSystemsFromInventory(inventory) {
  const rows = inventory?.external_systems;
  if (!Array.isArray(rows)) return [];
  return rows.map(normalizeAgentSystem).filter(Boolean);
}

function nodeLooksGeneric(node) {
  const id = String(node?.id || '').toLowerCase();
  const label = String(node?.label || '').toLowerCase();
  return /(_hub|hub_|^rfc$|^idoc$|^odata$|^interface)/.test(id)
    || /(_hub|hub_)/.test(label);
}

function nodeFromDiagramEntry(node) {
  if (!node || typeof node !== 'object') return null;
  const label = String(node.label || node.id || '').trim();
  if (!label || nodeLooksGeneric(node)) return null;
  const coreTypes = new Set(['sap', 's4', 'db', 'os', 'system']);
  const layer = String(node.layer || '').toLowerCase();
  const type = String(node.type || '').toLowerCase();
  if (coreTypes.has(type) && ['application', 'core', 'infra', 'platform'].includes(layer)) {
    return null;
  }
  const id = String(node.system_id || node.id || slugId(label)).trim();
  const description = String(node.description || '').trim();
  const identificationSource = String(node.source || node.provenance || '').trim();
  return {
    id,
    name: label,
    description: description || identificationSource || `${label} appears in this diagram.`,
    category: 'Connected system',
    role: 'other',
    identificationSource: identificationSource || 'Named in Analysis Agent diagram',
    nodeIds: [String(node.id || id).trim()].filter(Boolean),
    fromAgent: Boolean(description || identificationSource),
  };
}

function uniqById(list) {
  const map = new Map();
  list.forEach((sys) => {
    if (!sys?.id) return;
    const prev = map.get(sys.id);
    if (!prev) {
      map.set(sys.id, { ...sys, nodeIds: [...(sys.nodeIds || [])] });
      return;
    }
    const nodeIds = new Set([...(prev.nodeIds || []), ...(sys.nodeIds || [])]);
    map.set(sys.id, {
      ...prev,
      description: prev.description || sys.description,
      identificationSource: prev.identificationSource || sys.identificationSource,
      nodeIds: [...nodeIds],
    });
  });
  return [...map.values()];
}

/**
 * Full glossary for the current project view.
 * Primary: inventory.external_systems from the LLM.
 * Fallback: diagram nodes with inline description/source (older runs).
 */
export function resolveExternalSystemsGlossary({ inventory, nodes } = {}) {
  const fromInventory = getExternalSystemsFromInventory(inventory);
  if (fromInventory.length) {
    if (!Array.isArray(nodes) || !nodes.length) return fromInventory;
    const byNodeId = new Map();
    fromInventory.forEach((sys) => {
      (sys.nodeIds || []).forEach((nid) => byNodeId.set(nid, sys.id));
    });
    return fromInventory;
  }

  if (!Array.isArray(nodes)) return [];
  return uniqById(
    nodes.map(nodeFromDiagramEntry).filter(Boolean),
  );
}

/** Systems referenced by the nodes visible in a diagram (subset of glossary). */
export function externalSystemsForDiagram({ inventory, nodes } = {}) {
  const glossary = resolveExternalSystemsGlossary({ inventory, nodes });
  if (!Array.isArray(nodes) || !nodes.length) return glossary;

  const nodeIds = new Set(nodes.map((n) => String(n?.id || '').trim()).filter(Boolean));
  const labels = new Set(
    nodes.map((n) => String(n?.label || '').trim().toLowerCase()).filter(Boolean),
  );

  return glossary.filter((sys) => {
    if ((sys.nodeIds || []).some((nid) => nodeIds.has(nid))) return true;
    return labels.has(String(sys.name || '').toLowerCase());
  });
}

/** Resolve a diagram node to its glossary entry (by system_id, node id, or label). */
export function lookupExternalSystemForNode(node, { inventory, glossary } = {}) {
  if (!node) return null;
  const rows = glossary || resolveExternalSystemsGlossary({ inventory });
  if (!rows.length) return null;

  const systemId = String(node.system_id || '').trim();
  if (systemId) {
    const hit = rows.find((s) => s.id === systemId);
    if (hit) return hit;
  }

  const nodeId = String(node.id || '').trim();
  if (nodeId) {
    const hit = rows.find((s) => (s.nodeIds || []).includes(nodeId));
    if (hit) return hit;
  }

  const label = String(node.label || '').trim().toLowerCase();
  if (label) {
    const hit = rows.find((s) => String(s.name || '').trim().toLowerCase() === label);
    if (hit) return hit;
  }

  if (node.description || node.source) {
    return {
      id: systemId || nodeId || slugId(node.label),
      name: node.label || node.id,
      description: String(node.description || node.source || '').trim(),
      category: 'Connected system',
      identificationSource: String(node.source || node.provenance || '').trim(),
      fromAgent: true,
    };
  }

  return null;
}

/** @deprecated use resolveExternalSystemsGlossary */
export function collectMentionedExternalSystems(ctx) {
  return resolveExternalSystemsGlossary(ctx);
}

/** @deprecated use lookupExternalSystemForNode */
export function lookupExternalSystem(label, inventory) {
  const rows = resolveExternalSystemsGlossary({ inventory });
  const hay = String(label || '').trim().toLowerCase();
  if (!hay) return null;
  return rows.find((s) => String(s.name || '').toLowerCase().includes(hay)
    || String(s.id || '').toLowerCase() === hay) || null;
}
